import numeral from 'numeral';

/**
 * One headline number: its label, its value and how that value moved.
 *
 * `change` is a fraction rendered as a percentage, which is what a trend slope or a plain
 * period-over-period ratio gives. A metric whose movement is not a ratio — percentage points,
 * ranks — passes `changeLabel` with the text already written in its own unit, and carries its own
 * direction marker there, so the arrow icon is dropped rather than contradicting it. A metric
 * where the smaller number is the better one passes `changeDirection` so the colour follows
 * whether the movement was good instead of whether it was upward.
 *
 * `note` is a line of context under the value, and `onClick` turns the whole tile into a button —
 * which is how a row of metrics selects what the chart beside it draws.
 */
function Metric(props) {
    const Wrapper = props.onClick ? 'button' : 'div';
    const wrapperProps = props.onClick
        ? { type: 'button', onClick: props.onClick, 'aria-pressed': !!props.selected }
        : {};

    const classes = [
        'metric',
        props.metricType === 'primary' ? 'with-pattern' : 'without-pattern',
        props.onClick ? 'selectable' : '',
        props.selected ? 'selected' : '',
    ]
        .filter(Boolean)
        .join(' ');

    // A metric with nothing in it reads as missing, not as a zero.
    const value =
        props.value === null || props.value === undefined
            ? '—'
            : numeral(props.value).format(props.format || '0,00');

    // An explicit null changeLabel is a metric with no movement to report; leaving the prop out
    // keeps the long-standing behaviour of always drawing the percentage.
    const showChange = props.changeLabel !== undefined ? props.changeLabel !== null : true;
    const direction = props.changeDirection ?? (props.change > 0 ? 'up' : 'down');

    const change = showChange && (
        <small className={`percent-box ${direction ?? ''}`}>
            {!props.changeLabel && <i className="icon-arrow-right1"></i>}
            <span>{props.changeLabel ?? numeral(props.change).format('0.0%')}</span>
        </small>
    );

    return (
        <>
            {props?.metricType === 'primary' ? (
                <Wrapper className={classes} {...wrapperProps}>
                    {props.img && (
                        <div className="metric-img">
                            <img src={props.img} alt="img" />
                        </div>
                    )}
                    <div className="metric-body">
                        <div className="metric-label">
                            <div
                                className="metric-label-color-box"
                                style={{ background: props.color }}
                            ></div>
                            <div>{props.label}</div>
                        </div>
                        <div className="metric-bottom">
                            <h3 className="metric-value" style={{ fontFamily: 'helvetica' }}>
                                <span>{value}</span>
                            </h3>
                            {change}
                        </div>
                        {!!props.note && <div className="metric-note">{props.note}</div>}
                    </div>
                </Wrapper>
            ) : (
                <Wrapper
                    className={`${classes} d-inline-flex align-items-center`}
                    {...wrapperProps}
                >
                    <div className="info">
                        <h2 className="metric-name">
                            <div
                                className="metric-label-color-box"
                                style={{
                                    background: props.color,
                                    boxShadow: `0px 0px 10px ${props.color}`,
                                }}
                            ></div>
                            {props.label}{' '}
                            {props.info && (
                                <i
                                    title={props.info}
                                    className="icon-info cursor-pointer"
                                    style={{ marginLeft: '7px' }}
                                ></i>
                            )}
                        </h2>
                        <div className="d-flex align-items-center">
                            <h3 className="metric-value">{value}</h3>
                            {change}
                        </div>
                        {!!props.note && <div className="metric-note">{props.note}</div>}
                    </div>
                </Wrapper>
            )}
        </>
    );
}

export default Metric;
